import { useCmsNew, visibleT } from "@/lib/cms/cms-new-i18n";
import { useQuery } from "@tanstack/react-query";
import { getPrices, getPricesInsertion } from "@/api/cms";
import { Label } from "@/components/ui/label";
import {
  PuckSafeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/cms-puck/fields/PuckSafeSelect";
import {
  normalizeStripePaymentTarget,
  type PaymentButtonMode,
  type StripePaymentTarget,
} from "@/lib/cms-puck/blocks/pricing-payment";

type InstancePrice = {
  id: string;
  name?: Partial<Record<"fr" | "en" | "es" | "nl", string>>;
  name_fr?: string;
  name_en?: string;
  priceDescriptionFromStripe?: {
    price?: { unit_amount?: number | string };
    product?: { name?: string };
  };
};

function formatStripeAmount(cents: number | string | undefined): string {
  const n = typeof cents === "string" ? parseInt(cents, 10) : cents ?? 0;
  if (!Number.isFinite(n) || n <= 0) return "";
  return `${n / 100} €`;
}

function resolveProductDisplayName(p: InstancePrice): string {
  const stripeName = p.priceDescriptionFromStripe?.product?.name;
  if (typeof stripeName === "string" && stripeName.trim()) {
    return stripeName.trim();
  }
  const localized = p.name;
  if (localized && typeof localized === "object") {
    for (const loc of ["fr", "en", "es", "nl"] as const) {
      const val = localized[loc];
      if (typeof val === "string" && val.trim()) return val.trim();
    }
  }
  const legacy = p.name_fr || p.name_en;
  if (typeof legacy === "string" && legacy.trim()) return legacy.trim();
  return visibleT("cms.new.pickers.stripePrice.unnamedProduct", "Produit sans nom");
}

function priceOptionLabel(p: InstancePrice): string {
  const name = resolveProductDisplayName(p);
  const amount = formatStripeAmount(
    p.priceDescriptionFromStripe?.price?.unit_amount,
  );
  return amount ? `${name} (${amount})` : name;
}

/** Sélection du mode Stripe et du prix de l'instance. */
export function StripePricePickerField({
  value,
  onChange,
}: {
  value?: StripePaymentTarget;
  onChange: (v: StripePaymentTarget) => void;
}) {
  const cmsNew = useCmsNew();
  const payment = normalizeStripePaymentTarget(value);
  const mode = payment.mode;

  const { data: prices = [], isLoading } = useQuery<InstancePrice[]>({
    queryKey: ["puck-stripe-price-picker", mode],
    queryFn: () =>
      mode === "insertion" ? getPricesInsertion() : getPrices(),
  });

  const update = (patch: Partial<StripePaymentTarget>) =>
    onChange({ ...payment, ...patch });

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{cmsNew("cms.new.auto.text.type_de_paiement", "Type de paiement")}</Label>
        <Select
          value={mode}
          onValueChange={(v) =>
            update({
              mode: v as PaymentButtonMode,
              priceId: "",
            })
          }
        >
          <SelectTrigger className="h-9 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="insertion">{cmsNew("cms.new.auto.text.insertion_paiement", "Insertion (paiement)")}</SelectItem>
            <SelectItem value="consultation">
              {cmsNew("cms.new.pickers.stripePrice.modeConsultation", "Consultation (abonnement)")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      {mode === "insertion" ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <p>
            {cmsNew(
              "cms.new.pickers.stripePrice.insertionHintBefore",
              "Le bouton renvoie vers le parcours d'authentification d'œuvres (",
            )}
            <code className="text-[10px]">/expertise/introduction</code>
            {cmsNew(
              "cms.new.pickers.stripePrice.insertionHintAfter",
              ") avec le produit sélectionné, comme sur la page Expertise de l'ancien CMS.",
            )}
          </p>
        </div>
      ) : null}
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{cmsNew("cms.new.auto.text.produit_stripe", "Produit Stripe")}</Label>
        {isLoading ? (
          <p className="text-sm text-slate-400">{cmsNew("cms.new.auto.text.chargement_des_prix", "Chargement des prix…")}</p>
        ) : !prices.length ? (
          <p className="text-sm text-amber-600">
            {cmsNew(
              "cms.new.pickers.stripePrice.noPriceConfigured",
              "Aucun prix configuré pour ce mode dans l'instance.",
            )}
          </p>
        ) : (
          <Select
            value={payment.priceId || "__none__"}
            onValueChange={(v) =>
              update({ priceId: v === "__none__" ? "" : v })
            }
          >
            <SelectTrigger className="h-9 bg-white">
              <SelectValue placeholder={cmsNew("cms.new.auto.placeholder.selectionner_un_prix", "Sélectionner un prix…")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">{cmsNew("cms.new.pickers.stripePrice.noneOption", "— Aucun —")}</SelectItem>
              {prices.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {priceOptionLabel(p)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}
